/**
 * useGlobalKeyboardShortcuts — single window-keydown listener for app-wide
 * keyboard shortcuts. Currently only Shift+Ctrl+D for the admin-only
 * Parallel Calculation Debug Panel (Day 47, Decision 16). Future shortcuts
 * (e.g. /, ?, command palette) extend the inner switch.
 *
 * Layer 1 of the three-layer admin guard for the Debug Panel:
 *  - Layer 1: this hook gates the shortcut on isAdmin (silent for non-admins)
 *  - Layer 2: ParallelCalcDebugPanel returns null when !isAdmin
 *  - Layer 3: calculate_breakdown_extras RPC raises 'admin only' exception
 */
import { useEffect } from 'react';
import { useApp } from './useApp';
import { useDebugPanel } from './useDebugPanel';

export function useGlobalKeyboardShortcuts(): void {
  const { isAdmin } = useApp();
  const { togglePanel } = useDebugPanel();

  useEffect(() => {
    const handler = (event: KeyboardEvent): void => {
      // Shift+Ctrl+D — toggle Debug Panel (admin only).
      // Use event.code (not event.key) so shifted-D doesn't depend on
      // keyboard layout. preventDefault to suppress browser default
      // (Add Bookmark on Chrome/Edge, Toggle Bookmarks Bar on Firefox).
      if (event.shiftKey && event.ctrlKey && event.code === 'KeyD') {
        if (!isAdmin) return; // Layer 1 guard — silently inactive for non-admin
        event.preventDefault();
        togglePanel();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isAdmin, togglePanel]);
}
